/**
 * MemberEngagementHistoryTab — renders the per-member campaign and engagement
 * history backed by /api/members/{id}/engagement-history, plus the engagement
 * intelligence summary (score + churn-risk predictions across 7/14/30-day
 * horizons) backed by /api/members/{id}/engagement-prediction.
 *
 * This is the UUHP-side equivalent of the retention dashboard panels Trust
 * Care uses; the prediction model is the same rule-based engine seeded by
 * migration 343 for cbo_id=303.
 */

import React, { useEffect, useState } from 'react';
import { authFetchJson } from '@/lib/authFetch';
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Phone,
  MessageSquare,
  Mail,
  XCircle,
} from 'lucide-react';

interface EngagementHistoryRow {
  campaign_id: string;
  campaign_name: string;
  lob_id: number | null;
  product_id: number | null;
  lob_name: string | null;
  product_name: string | null;
  engagement_order: number | null;
  engagement_id: string | null;
  engagement_name: string | null;
  engagement_type: string | null;
  first_event_type: string | null;
  first_occurred_at: string | null;
  channel: string | null;
  language: string | null;
  last_event_type: string | null;
  last_occurred_at: string | null;
  event_count: number;
}

interface MLPrediction {
  prediction_horizon_days: number;
  churn_probability: number;
  will_churn_predicted: boolean;
  risk_level: 'low' | 'moderate' | 'high' | 'critical';
  contributing_factors: Array<{ factor: string; value: any; importance: number; impact: string }> | null;
  prediction_date: string | null;
  model_id: string;
}

interface RawMetrics {
  has_data: boolean;
  sent: number;
  delivered: number;
  responded: number;
  opened: number;
  bounced: number;
  opted_out: number;
  last_event_at: string | null;
  campaigns_touched: number;
}

interface EngagementPredictionPayload {
  engagement_score: null | {
    engagement_score: number;
    risk_level: string;
    trend_direction: string;
    trend_velocity: number;
    communication_score: number;
    activity_score: number;
    recency_score: number;
    data_quality_score: number;
    days_since_last_contact: number;
    response_rate_30d: number;
    total_contact_attempts_30d: number;
    successful_contacts_30d: number;
    last_calculated_at: string;
  };
  predictions: MLPrediction[];
  raw_metrics?: RawMetrics;
  calculation_notes?: string;
}

const channelIcon = (channel: string | null) => {
  if (channel === 'sms') return <MessageSquare className="h-3.5 w-3.5" />;
  if (channel === 'voice') return <Phone className="h-3.5 w-3.5" />;
  if (channel === 'email') return <Mail className="h-3.5 w-3.5" />;
  return <MessageSquare className="h-3.5 w-3.5 opacity-50" />;
};

const eventBadge = (eventType: string | null) => {
  if (!eventType) return null;
  const cls =
    eventType === 'delivered'
      ? 'bg-green-100 text-green-800'
      : eventType === 'responded'
      ? 'bg-blue-100 text-blue-800'
      : eventType === 'opened'
      ? 'bg-sky-100 text-sky-800'
      : eventType === 'bounced'
      ? 'bg-red-100 text-red-800'
      : eventType === 'opted_out'
      ? 'bg-amber-100 text-amber-900'
      : 'bg-gray-100 text-gray-700';
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{eventType}</span>
  );
};

const riskColor = (level: string | undefined | null) => {
  switch (level) {
    case 'critical':
    case 'high':
      return 'text-red-700 bg-red-50 border-red-200';
    case 'at_risk':
    case 'moderate':
    case 'watch':
      return 'text-amber-800 bg-amber-50 border-amber-200';
    case 'healthy':
    case 'low':
      return 'text-green-700 bg-green-50 border-green-200';
    default:
      return 'text-gray-700 bg-gray-50 border-gray-200';
  }
};

export const MemberEngagementHistoryTab: React.FC<{
  memberId: number;
  cboId: number;
}> = ({ memberId, cboId }) => {
  const [history, setHistory] = useState<EngagementHistoryRow[]>([]);
  const [prediction, setPrediction] = useState<EngagementPredictionPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authFetchJson<EngagementHistoryRow[]>(
        `/api/members/${memberId}/engagement-history?cbo_id=${cboId}&limit=100`,
      ).catch(() => []),
      authFetchJson<EngagementPredictionPayload>(
        `/api/members/${memberId}/engagement-prediction?cbo_id=${cboId}`,
      ).catch(() => ({ engagement_score: null, predictions: [] })),
    ])
      .then(([h, p]) => {
        setHistory(Array.isArray(h) ? h : []);
        setPrediction(p);
      })
      .finally(() => setLoading(false));
  }, [memberId, cboId]);

  const score = prediction?.engagement_score;
  const predictions = prediction?.predictions || [];
  const raw = prediction?.raw_metrics;
  const hasData = raw?.has_data === true;

  // Group history rows under their campaign
  const grouped = history.reduce<Record<string, EngagementHistoryRow[]>>((acc, row) => {
    acc[row.campaign_id] = acc[row.campaign_id] || [];
    acc[row.campaign_id].push(row);
    return acc;
  }, {});

  const sortedCampaigns = Object.entries(grouped)
    .map(([id, rows]) => ({
      id,
      name: rows[0].campaign_name,
      lob_name: rows[0].lob_name,
      product_name: rows[0].product_name,
      rows: rows.sort((a, b) => (a.engagement_order || 0) - (b.engagement_order || 0)),
      latest: rows
        .map(r => r.last_occurred_at)
        .filter(Boolean)
        .sort()
        .reverse()[0] || null,
    }))
    .sort((a, b) => (b.latest || '').localeCompare(a.latest || ''));

  return (
    <div className="space-y-5">
      {/* Engagement Intelligence Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">
          Engagement intelligence
        </h3>
        {!hasData ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            <p className="font-semibold text-gray-700">No engagement data yet for this member.</p>
            <p className="mt-1">
              The engagement score and churn-prediction model both require
              at least one campaign touch (sent / delivered / responded /
              bounced event) before they can score this member. Start a
              campaign or wait for the next scheduled wave; the score
              recomputes nightly.
            </p>
            {raw && raw.campaigns_touched === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Diagnostic: <span className="font-mono">engagement_events</span> has 0 rows for this member.
              </p>
            )}
          </div>
        ) : !score && predictions.length === 0 ? (
          <p className="text-sm text-gray-500">
            Engagement events exist for this member but the scorer hasn&apos;t
            run yet — try again after the nightly batch.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            {score && (
              <div className={`rounded-lg border p-3 ${riskColor(score.risk_level)}`}>
                <div className="text-[10px] font-bold uppercase opacity-70">Engagement score</div>
                <div className="mt-0.5 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{Number(score.engagement_score).toFixed(0)}</span>
                  <span className="text-xs opacity-70">/ 100</span>
                </div>
                <div className="mt-1 text-xs">
                  Risk: <strong className="uppercase">{score.risk_level}</strong>
                  {score.trend_direction && score.trend_direction !== 'stable' ? (
                    <span className="ml-1 opacity-70">· {score.trend_direction}</span>
                  ) : null}
                </div>
              </div>
            )}
            {predictions.map(p => (
              <div key={p.prediction_horizon_days}
                className={`rounded-lg border p-3 ${riskColor(p.risk_level)}`}>
                <div className="text-[10px] font-bold uppercase opacity-70 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Churn — {p.prediction_horizon_days}-day
                </div>
                <div className="mt-0.5 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">
                    {(Number(p.churn_probability) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="mt-1 text-xs">
                  {p.will_churn_predicted ? (
                    <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Predicted churn</span>
                  ) : (
                    <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Likely to retain</span>
                  )}
                  <span className="ml-1 opacity-70 uppercase">· {p.risk_level}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Raw event counts powering the score */}
        {hasData && raw && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="mb-2 text-[10px] font-bold uppercase text-gray-500">
              Underlying engagement events (last 30 days unless otherwise noted)
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 md:grid-cols-7">
              <div className="rounded bg-gray-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-gray-500">Sent</div>
                <div className="font-mono text-sm font-semibold">{raw.sent}</div>
              </div>
              <div className="rounded bg-green-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-green-700">Delivered</div>
                <div className="font-mono text-sm font-semibold text-green-900">{raw.delivered}</div>
              </div>
              <div className="rounded bg-blue-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-blue-700">Responded</div>
                <div className="font-mono text-sm font-semibold text-blue-900">{raw.responded}</div>
              </div>
              <div className="rounded bg-sky-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-sky-700">Opened</div>
                <div className="font-mono text-sm font-semibold text-sky-900">{raw.opened}</div>
              </div>
              <div className="rounded bg-red-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-red-700">Bounced</div>
                <div className="font-mono text-sm font-semibold text-red-900">{raw.bounced}</div>
              </div>
              <div className="rounded bg-amber-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-amber-800">Opted out</div>
                <div className="font-mono text-sm font-semibold text-amber-900">{raw.opted_out}</div>
              </div>
              <div className="rounded bg-gray-50 px-2 py-1.5">
                <div className="text-[9px] uppercase text-gray-500">Campaigns touched</div>
                <div className="font-mono text-sm font-semibold">{raw.campaigns_touched}</div>
              </div>
            </div>
            {prediction?.calculation_notes && (
              <details className="mt-2 text-[11px] text-gray-600">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  How this is calculated
                </summary>
                <p className="mt-1 rounded bg-gray-50 p-2 leading-relaxed">
                  {prediction.calculation_notes}
                </p>
              </details>
            )}
          </div>
        )}

        {/* Contributing factors from the 14-day prediction (the default horizon) */}
        {(() => {
          const horizon14 = predictions.find(p => p.prediction_horizon_days === 14);
          if (!horizon14 || !horizon14.contributing_factors) return null;
          return (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="mb-2 text-[10px] font-bold uppercase text-gray-500">
                Top contributing factors (14-day horizon)
              </div>
              <ul className="flex flex-wrap gap-2 text-xs">
                {horizon14.contributing_factors.slice(0, 5).map((f, i) => (
                  <li key={i}
                    className={`rounded px-2 py-1 ${
                      f.impact === 'high' ? 'bg-red-50 text-red-800' :
                      f.impact === 'medium' ? 'bg-amber-50 text-amber-900' :
                      'bg-gray-50 text-gray-700'
                    }`}>
                    <span className="font-mono opacity-70">{f.factor}</span>:{' '}
                    <strong>{String(f.value)}</strong>
                    <span className="ml-1 opacity-60">· importance {(f.importance * 100).toFixed(0)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
      </div>

      {/* Campaigns + engagements */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
            Campaigns & engagements
            <span className="ml-2 text-[10px] font-normal normal-case text-gray-400">
              Every campaign this member has received, expanded by engagement step
            </span>
          </h3>
        </div>

        {loading && <p className="p-4 text-sm text-gray-500">Loading…</p>}
        {!loading && sortedCampaigns.length === 0 && (
          <p className="p-4 text-sm text-gray-500">No campaign touches recorded for this member yet.</p>
        )}

        {sortedCampaigns.map(c => (
          <div key={c.id} className="border-b border-gray-100 last:border-b-0">
            <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">{c.name}</div>
                <div className="text-[11px] text-gray-500">
                  {c.lob_name ? <span>{c.lob_name}</span> : null}
                  {c.product_name ? <span> · {c.product_name}</span> : null}
                  {!c.lob_name && !c.product_name ? <span>Unassigned product line</span> : null}
                </div>
              </div>
              {c.latest && (
                <span className="text-[11px] text-gray-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last touch {new Date(c.latest).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-1.5 w-16">Step</th>
                  <th className="px-2 py-1.5">Engagement</th>
                  <th className="px-2 py-1.5">Channel</th>
                  <th className="px-2 py-1.5">First event</th>
                  <th className="px-2 py-1.5">Last event</th>
                  <th className="px-2 py-1.5 text-right">Touches</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r, idx) => (
                  <tr key={`${r.campaign_id}-${r.engagement_id ?? idx}`} className="border-t border-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">
                      {r.engagement_order ? `#${r.engagement_order}` : '—'}
                    </td>
                    <td className="px-2 py-2">
                      <div className="text-gray-900">{r.engagement_name || '(generic)'}</div>
                      {r.engagement_type && (
                        <div className="text-[10px] text-gray-500">{r.engagement_type}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        {channelIcon(r.channel)}
                        {r.channel || '—'}
                      </span>
                      {r.language && (
                        <span className="ml-1 text-[10px] uppercase opacity-60">· {r.language}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-600">
                      {eventBadge(r.first_event_type)}{' '}
                      <span className="ml-1 opacity-60">
                        {r.first_occurred_at
                          ? new Date(r.first_occurred_at).toLocaleDateString()
                          : '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-600">
                      {eventBadge(r.last_event_type)}{' '}
                      <span className="ml-1 opacity-60">
                        {r.last_occurred_at
                          ? new Date(r.last_occurred_at).toLocaleDateString()
                          : '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-gray-700">{r.event_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MemberEngagementHistoryTab;
